import React, { useEffect, useMemo, useState } from 'react';

import './Integrations.scss';
import { useSelector } from 'react-redux';
import { Button, Card, Divider, Form, InputNumber, Popconfirm, Select, Spin, Tooltip, Switch } from 'antd';
import { useDispatch } from 'react-redux';

import { useForm } from 'antd/es/form/Form';
import { DeleteTwoTone, QuestionCircleTwoTone } from '@ant-design/icons';
import { selectAllCarrierIntegrations } from '../../../modules/carriers/stores/slice';

import {
  doCreateCarrierRule,
  doDeleteCarrierRule,
  doGetCarrierRule,
  doUpdateCarrierRule,
} from '../../../modules/carrier-rules/store/thunk';
import { clearState } from '../../../modules/carrier-rules/store/slice';
import { doGetAllDeliveryAreas, doUpdateDeliveryArea } from '../../../modules/delivery-areas/stores/thunk';
import { selectAllDeliveryAreas } from '../../../modules/delivery-areas/stores/slice';
import { doGetCarrierIntegrations } from '../../../modules/carriers/stores/thunk';

// NOTE: WHAT'S CALLED CARRIERS HERE ARE ACTUALLY CARRIER INTEGRATIONS: SHOULD FIX THIS

export const CarrierRules = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const [deliveryAreaForm] = useForm();

  const [carrier, setCarrier] = useState(null);
  const [deliveryArea, setDeliveryArea] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [suffixes, setSuffixes] = useState([]);
  const listSuffix = (i) => (p, c) => {
    const suffix = c?.restrictions?.[i]?.field === 'weight' ? 'gr' : 'mm';
    const ns = [...suffixes];
    ns[i] = suffix;
    setSuffixes(ns);
  };

  const carriers = useSelector((state) => selectAllCarrierIntegrations(state));
  const deliveryAreas = useSelector((state) => selectAllDeliveryAreas(state));
  const carrierRule = useSelector((state) => state.carrierRules.carrierRule);

  const carrierOptions = useMemo(() => (carriers || []).map((c) => ({ label: c.name, value: c.id })), [carriers]);
  const deliveryAreaOptions = useMemo(
    () => (deliveryAreas || []).map((d) => ({ label: d.name, value: d.id })),
    [deliveryAreas],
  );

  useEffect(() => {
    const fetchCarriers = async () => {
      await dispatch(doGetCarrierIntegrations({ page: 1, take: 1000 }));
    };
    fetchCarriers();
  }, []);

  useEffect(() => {
    if (carrier) {
      dispatch(doGetAllDeliveryAreas({ carrierIntegrationId: carrier.id }));
    }
  }, [carrier]);

  useEffect(() => {
    if (deliveryArea) {
      dispatch(doGetCarrierRule({ deliveryAreaId: deliveryArea.id }));
      deliveryAreaForm.setFieldValue('mailbox', deliveryArea.mailbox);
      deliveryAreaForm.setFieldValue('notAtNeighbour', deliveryArea.notAtNeighbour);
      deliveryAreaForm.setFieldValue('pickupPoint', deliveryArea.pickupPoint);
      deliveryAreaForm.setFieldValue('signature', deliveryArea.signature);
    }
  }, [deliveryArea]);

  useEffect(() => {
    return () => dispatch(clearState()); // clear state on unmount
  }, []);

  useEffect(() => {
    if (carrierRule?.id) {
      form.setFieldsValue(carrierRule);
    }
  }, [carrierRule]);

  const onFinish = async (data) => {
    setIsFetching(true);

    const params = { deliveryAreaId: deliveryArea.id, ...data };
    if (carrierRule?.id) {
      await dispatch(doUpdateCarrierRule({ id: carrierRule.id, ...params }));
    } else {
      if (data) await dispatch(doCreateCarrierRule(params));
    }
    setIsFetching(false);
  };

  const onFinishDeliveryArea = async (data) => {
    setIsFetching(true);

    const params = { id: deliveryArea.id, ...data };

    await dispatch(doUpdateDeliveryArea(params));
    setIsFetching(false);
  };

  const removeRule = async () => {
    dispatch(doDeleteCarrierRule({ id: carrierRule.id }));
  };

  const fieldOptions = [
    { label: 'Width', value: 'width' },
    { label: 'Height', value: 'height' },
    { label: 'Length', value: 'length' },
    { label: 'Weight', value: 'weight' },
  ];
  const operatorOptions = [
    { label: '<=', value: 'LTE' },
    { label: '<', value: 'LT' },
    { label: '>=', value: 'GTE' },
    { label: '>', value: 'GT' },
  ];

  const [disabled, setDisabled] = useState(true);
  const handleFormChange = async () => {
    // Check if the form is valid
    return new Promise(() => {
      setTimeout(() => {
        form
          .validateFields()
          .then(() => {
            const r = form.getFieldValue('restrictions');

            if (r?.length > 0) {
              setDisabled(false);
            } else {
              setDisabled(true);
            }
          })
          .catch(() => setDisabled(true));
      });
    });
  };
  const combination = Form.useWatch('combination', form);

  return (
    <div className="info">
      <div className="info-content">
        <h3>Carrier Rules</h3>
        <h5>Configure rules to define limitiations of the carrier</h5>
      </div>

      <div>
        <h4>
          1. Select your carrier
          <Tooltip title="Select the location that you want your shopify store to create orders for">
            <QuestionCircleTwoTone style={{ marginLeft: 8 }} />
          </Tooltip>
        </h4>
        <div style={{ display: 'flex', justifyContent: 'left' }}>
          <Select
            className="select-small"
            options={carrierOptions}
            placeholder="Select a carrier"
            onSelect={(id) => setCarrier(carriers.find((l) => l.id === id))}
            style={{ minWidth: '200px' }}
          />
          <Select
            className="select-small"
            options={deliveryAreaOptions}
            placeholder="Select a delivery area"
            onSelect={(id) => setDeliveryArea(deliveryAreas.find((d) => d.id === id))}
            style={{ minWidth: '200px', marginLeft: 8 }}
          />
        </div>
      </div>
      {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}></div> */}
      {carrier && (
        <div>
          <Divider />
          {deliveryArea && (
            <Spin spinning={isFetching}>
              <Form
                form={form}
                scrollToFirstError
                onFinish={onFinish}
                layout="vertical"
                onValuesChange={handleFormChange}
              >
                <h4>2. Configure a set of restrictions</h4>

                <h5 style={{ marginBottom: 16 }}>Describe what the carrier can handle, eg: weight {'<'} 1000 gr</h5>
                <Form.Item
                  label="Parcels should meet"
                  name="combination"
                  rules={[{ required: true, message: 'Please select how to combine the restric' }]}
                >
                  <Select
                    className="select-small"
                    style={{ width: 300 }}
                    options={[
                      { label: 'ALL of the following', value: 'AND' },
                      { label: 'SOME of the following', value: 'OR' },
                    ]}
                  />
                </Form.Item>
                {combination && (
                  <Card style={{ paddingLeft: 16 }}>
                    <Form.List name="restrictions" label="Restirctions">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, i) => (
                            <div style={{ display: 'flex' }} key={field.key}>
                              <Form.Item
                                label="Dimension"
                                name={[field.name, 'field']}
                                rules={[{ required: true, message: 'Please select a dimension' }]}
                              >
                                <Select className="select-small" options={fieldOptions} style={{ width: 200 }} />
                              </Form.Item>

                              <Form.Item
                                label="Operator"
                                name={[field.name, 'operator']}
                                style={{ marginLeft: 16 }}
                                rules={[{ required: true, message: 'Please select an operator' }]}
                              >
                                <Select className="select-small" options={operatorOptions} style={{ width: 100 }} />
                              </Form.Item>
                              <Form.Item
                                label="Value"
                                name={[field.name, 'value']}
                                style={{ marginLeft: 16 }}
                                shouldUpdate={listSuffix(i)}
                                rules={[{ required: true, message: 'Please set a value to compare to' }]}
                              >
                                <InputNumber
                                  min={0}
                                  type="number"
                                  className="select-small"
                                  style={{ width: 100 }}
                                  suffix={suffixes[i]}
                                />
                              </Form.Item>

                              <DeleteTwoTone
                                style={{ marginLeft: 16, marginTop: 8 }}
                                twoToneColor="red"
                                onClick={() => {
                                  remove(field.name);
                                }}
                              />
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add()} size="small">
                            + Add restriction
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Card>
                )}

                <div style={{ display: 'flex', justifyContent: 'right', marginTop: 16 }}>
                  {carrierRule?.id && (
                    <Popconfirm
                      title="Are you sure"
                      description="you want to delete the rule?"
                      placement="top"
                      okText="Delete"
                      onConfirm={removeRule}
                    >
                      <Button
                        type="default"
                        size="small"
                        loading={isFetching}
                        style={{ color: 'red', borderColor: 'red' }}
                      >
                        Remove rule
                      </Button>
                    </Popconfirm>
                  )}
                  <Button type="primary" size="small" htmlType="submit" loading={isFetching} disabled={disabled}>
                    {carrierRule?.id ? 'Update' : 'Save'} rule
                  </Button>
                </div>
              </Form>
            </Spin>
          )}

          <Divider />
          {deliveryArea && (
            <Spin spinning={isFetching}>
              <Form
                form={deliveryAreaForm}
                scrollToFirstError
                onFinish={onFinishDeliveryArea}
                layout="vertical"
                onValuesChange={handleFormChange}
              >
                <h4>3. Capabilities</h4>

                <h5 style={{ marginBottom: 16 }}>Check the capabilites of this delivery area</h5>

                <div style={{ display: 'flex' }}>
                  <Form.Item name="signature">
                    <Switch />
                  </Form.Item>
                  <h5 style={{ marginLeft: 8, marginTop: 8 }}>Signature</h5>
                </div>
                <div style={{ display: 'flex' }}>
                  <Form.Item name="notAtNeighbour">
                    <Switch />
                  </Form.Item>
                  <h5 style={{ marginLeft: 8, marginTop: 8 }}>Not at neighbour</h5>
                </div>
                <div style={{ display: 'flex' }}>
                  <Form.Item name="mailbox">
                    <Switch />
                  </Form.Item>
                  <h5 style={{ marginLeft: 8, marginTop: 8 }}>Mailbox</h5>
                </div>
                <div style={{ display: 'flex' }}>
                  <Form.Item name="pickupPoint">
                    <Switch />
                  </Form.Item>
                  <h5 style={{ marginLeft: 8, marginTop: 8 }}>Pickup point</h5>
                </div>
                <div style={{ display: 'flex', justifyContent: 'right', marginTop: 16 }}>
                  <Button type="primary" size="small" htmlType="submit" loading={isFetching}>
                    Save capabilities
                  </Button>
                </div>
              </Form>
            </Spin>
          )}
        </div>
      )}
    </div>
  );
};
